import * as Sentry from '@sentry/react'
import { useCallback, useEffect, useRef, useState } from 'react'

import { useRouteCompanyId } from '@/hooks/use-route-company-id'
import { NetworkError } from '@/services/fintalk/api/types/errors/NetworkError'
import {
  listProjects,
  type Project,
} from '@/services/fintalk/services/fintalk-backoffice/projects/list'

type GetCompanyProjectsLoadingState = { type: 'loading' }

type GetCompanyProjectsSuccessState = { type: 'success' }

type GetCompanyProjectsErrorState = {
  type: 'error'
  errorType: 'NETWORK_ERROR' | 'UNKNOWN_ERROR'
  eventId?: string
  isRetrying?: boolean
}

export type GetCompanyProjectsState =
  | GetCompanyProjectsLoadingState
  | GetCompanyProjectsSuccessState
  | GetCompanyProjectsErrorState

export function useGetCompanyProjects() {
  const companyId = useRouteCompanyId()!

  const [projects, setProjects] = useState<Project[]>([])
  const [projectsStatus, setProjectsStatus] = useState<GetCompanyProjectsState>(
    {
      type: 'loading',
    },
  )

  const latestRequest = useRef(0)

  const updateProjectInProjects = useCallback((project: Project) => {
    setProjects((projects) =>
      projects.map((item) => (item._id === project._id ? project : item)),
    )
  }, [])

  const loadProjects = useCallback(async () => {
    const request = ++latestRequest.current

    setProjects([])
    setProjectsStatus((status) =>
      status.type === 'error'
        ? { ...status, isRetrying: true }
        : { type: 'loading' },
    )

    try {
      const projects = await listProjects(companyId)

      if (request !== latestRequest.current) return

      setProjects(projects)
      setProjectsStatus({ type: 'success' })
    } catch (error) {
      if (request !== latestRequest.current) return

      if (error instanceof NetworkError) {
        setProjectsStatus({ type: 'error', errorType: 'NETWORK_ERROR' })
        return
      }

      const eventId = Sentry.captureException(error)

      setProjectsStatus({ type: 'error', errorType: 'UNKNOWN_ERROR', eventId })
    }
  }, [companyId])

  useEffect(() => {
    loadProjects()
  }, [loadProjects])

  return { projects, projectsStatus, loadProjects, updateProjectInProjects }
}
